// "Continue with Google / Facebook": a pop-up on laptops, a full-page
// redirect on phones and in in-app browsers (Facebook / Instagram), where
// pop-ups often hang or never come back.
//
// With a redirect the page reloads, so what the page needs to carry on is
// saved in sessionStorage first (which page, which provider, extra bits such
// as the "delete if inactive" tick); on its return the page calls
// takeRedirectSignIn(page) and finishes as it would after a pop-up.
//
// Redirects only work on the live site, where the login runs through
// theinterconnector.com itself (Firebase.js authDomain). On localhost it runs
// through firebaseapp.com — a different site — and browsers' storage rules
// (Chrome, Safari, Firefox) lose the result on the way back, so redirects
// can't be tested locally — locally it's always the pop-up. On the live site
// ?redirecttest=1 forces a redirect (to test it), ?redirecttest=0 turns that
// off again.
import { auth } from '../Firebase';
import { isInAppBrowser } from './inAppBrowser';

const PENDING_KEY = 'icPendingSignIn';
const TEST_KEY = 'icRedirectTest';

const forced = () => {
  try {
    const flag = new URLSearchParams(window.location.search).get('redirecttest');
    if (flag === '1') sessionStorage.setItem(TEST_KEY, '1');
    if (flag === '0') sessionStorage.removeItem(TEST_KEY);
    return sessionStorage.getItem(TEST_KEY) === '1';
  } catch {
    return false;
  }
};

const isPhone = () => /Android|iPhone|iPad|iPod|Mobile/i.test((typeof navigator !== 'undefined' && navigator.userAgent) || '');
const onLiveDomain = () => typeof window !== 'undefined' && window.location.hostname === 'theinterconnector.com';

// Redirects are on inside in-app browsers (Facebook, Instagram…), where a
// pop-up hangs or never comes back — in Oct 2026 every "Continue with
// Facebook" there ended with nothing. Elsewhere (laptops, phones' own
// browsers) the pop-up works, so it stays. ?redirecttest=1 still forces one.
const AUTO_REDIRECT = true;

// Live site only: elsewhere the result would be lost (see the top of this file).
export const prefersRedirect = () => onLiveDomain() && (forced() || (AUTO_REDIRECT && isInAppBrowser()));

// Sign in with `provider`. Pop-up: resolves to the result. Redirect: saves
// { page, provider: 'google' | 'facebook', ...extra } and leaves the page —
// resolves to null (nothing more to do until it comes back).
export const signInWith = async (provider, { page, extra } = {}) => {
  if (!prefersRedirect()) return auth.signInWithPopup(provider);
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({
      page,
      provider: provider.providerId === 'facebook.com' ? 'facebook' : 'google',
      at: Date.now(),
      ...extra,
    }));
  } catch { /* storage blocked: the result is still picked up below */ }
  await auth.signInWithRedirect(provider);
  return null;
};

// Whether this page load is the return from a redirect started by `page` —
// checked straight away, so the page can show "Signing you in…" instead of
// flashing its sign-in form while takeRedirectSignIn finishes.
export const hasPendingRedirect = (page) => {
  try {
    const pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null');
    return !!pending && pending.page === page && Date.now() - (pending.at || 0) <= 30 * 60 * 1000;
  } catch {
    return false;
  }
};

// One getRedirectResult per page load, shared by every component on it.
let redirectResult = null;
const getRedirectResultOnce = () => {
  if (!redirectResult) redirectResult = auth.getRedirectResult().then(r => ({ result: r }), error => ({ error }));
  return redirectResult;
};

// On return from a redirect started by `page`: { pending, result } or
// { pending, error } (e.g. Facebook's "account exists" error), else null.
// The saved state is cleared, so it's handled once.
export const takeRedirectSignIn = async (page) => {
  let pending = null;
  try { pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); } catch { /* none */ }
  if (!pending || pending.page !== page) return null;
  // Older than 30 minutes: they wandered off; don't act on it now.
  if (Date.now() - (pending.at || 0) > 30 * 60 * 1000) {
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
    return null;
  }
  const { result, error } = await getRedirectResultOnce();
  try { sessionStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
  if (error) return { pending, error };
  if (result?.user) return { pending, result };
  return null;
};
